import React, { useState, useRef, useEffect } from "react";
import "./userUpdate.styles.scss";
import { updateProfile, disableProfile } from "../../service/profile/profile";
import { Input, Select, Avatar, Button, Form } from "@alpaca/framework";
import { fetchPermissionsForUser } from "../../service/permission/permission";
import _ from "lodash";
import Permissions from "../user/permission/permission.component";
import DisableConfirmation from "./userDisableConfirmation.component";

import {
  formatPhoneNumber,
  validPhoneNumberFormat,
} from "../../helpers/phoneNumberFormat";

export function UserUpdate(props) {
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [setStatus, status] = useState("Active");
  const fullName = `${props.rowData?.first_name || ""} ${
    props.rowData?.last_name || ""
  }`;
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchPermissions = async (companyName, profileName) => {
      try {
        const { company_name, profile_name } = props.rowData;
        const res = await fetchPermissionsForUser(company_name, profile_name);
        const data = res.data.map((el) => ({
          ...el,
          isGranted: el.profile_permission_group_xref_id !== null,
        }));
        setOriginalPermissions(data);
        setPermissions(data);
      } catch (error) {
        props.onUpdate({
          toast: {
            title: "Update Failed",
            description: error.message,
            duration: 3,
            status: "error",
          },
        });
      }
    };
    fetchPermissions();
  }, [props.rowData]);

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };
  const closeModal = (e) => {
    e?.preventDefault();
    props.onClose(false);
  };

  const handleChangePermission = (index, value) => {
    setPermissions((prev) => {
      const curr = _.cloneDeep(prev);
      curr[index].isGranted = value;
      return curr;
    });
  };

  const getChangedPermissions = () => {
    const result = [];
    for (let i = 0; i < originalPermissions.length; i++) {
      if (originalPermissions[i].isGranted !== permissions[i].isGranted) {
        result.push(permissions[i]);
      }
    }
    return result;
  };

  const onCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleDisableProfile = async (e) => {
    e.preventDefault();

    const disabledProfile = {
      profile_name: props.rowData?.profile_name,
      company_name: props.rowData?.company_name,
    };

    try {
      const result = await disableProfile(disabledProfile);
      props.onUpdate({
        toast: {
          title: "User Disabled.",
          description: result.message,
          duration: 3,
          status: "success",
        },
      });

      closeModal();
      props.removeUser(disabledProfile);
    } catch (error) {
      props.onUpdate({
        toast: {
          title: "Update Failed",
          description: error.message,
          duration: 3,
          status: "error",
        },
      });
    }
  };

  const save = async (e) => {
    const formValues = await form.validateFields();
    const updatedProfile = {
      profile_name: props.rowData?.profile_name,
      company_name: props.rowData?.company_name,
      first_name: formValues.first_name,
      last_name: formValues.last_name,
      email_address: formValues.email_address,
      phone_number: formValues.phone_number,
      pin: formValues.pin,
    };
    const updatedData = {
      ...updatedProfile,
      changedPermissions: getChangedPermissions(),
    };

    try {
      const result = await updateProfile(updatedData);
      props.onUpdate({
        toast: {
          title: "Record Updated",
          description: result.message,
          duration: 3,
          status: "success",
        },
      });
      props.updateUser(updatedProfile);
      closeModal();
    } catch (error) {
      props.onUpdate({
        toast: {
          title: "Update Failed",
          description: error.message,
          duration: 3,
          status: "error",
        },
      });
    }
  };
  return (
    <>
      <Form
        form={form}
        onFinish={save}
        style={{ height: "100%" }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
      >
        <div className="userSettingsContainer">
          <div className="userSettingsModalBody">
            <div className="userSettingsDetails">
              <div className="userSettingsProfile">
                <header className="userSettingsHeader">
                  <div className="userSettingsAvatarContainer">
                    <Avatar name={`${fullName || ""}`} />
                  </div>
                  <h2 className="pageTitle">{fullName}</h2>
                </header>
                <div className="userSettingsForm">
                  <div className="userSettingsFromRow">
                    <div className="userSettingFormItem">
                      <Form.Item
                        name="first_name"
                        initialValue={props.rowData?.first_name}
                        label="First Name"
                        labelCol={{ span: 24 }}
                        style={{ margin: 0 }}
                      >
                        <Input
                          title="First Name:"
                          className="user-input"
                          required
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </div>

                    <div className="userSettingFormItem">
                      <Form.Item
                        initialValue={props.rowData?.last_name}
                        name="last_name"
                        label="Last Name:"
                        labelCol={{ span: 24 }}
                        style={{ margin: 0 }}
                      >
                        <Input
                          title="Last Name"
                          className="user-input"
                          required
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="userSettingsFromRow">
                    <div className="userSettingFormItem">
                      <Form.Item
                        initialValue={props.rowData?.email_address}
                        name="email_address"
                        label="Email"
                        labelCol={{ span: 24 }}
                        style={{ margin: 0 }}
                      >
                        <Input title="Email" className="user-input" required />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="userSettingsFromRow">
                    <Form.Item
                      initialValue={props.rowData?.phone_number}
                      name="phone_number"
                      label="Phone Number"
                      labelCol={{ span: 24 }}
                      normalize={(value) => formatPhoneNumber(value)}
                      rules={[
                        {
                          pattern: validPhoneNumberFormat,
                          message: "Invalid phone number",
                        },
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input className="user-input" />
                    </Form.Item>

                    <Form.Item
                      initialValue={props.rowData?.pin}
                      name="pin"
                      label="PIN"
                      labelCol={{ span: 24 }}
                      style={{ margin: 0 }}
                    >
                      <Input title="PIN" className="user-input" />
                    </Form.Item>
                  </div>

                  <div className="userSettingsFromRow">
                    <div className="userSettingFormItem">
                      <label>Status:</label>
                      <Select style={{ width: "100%" }}>
                        <Select.Option value="Active">Active</Select.Option>
                        <Select.Option value="inactive">Inactive</Select.Option>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="userSettingsDetails">
              <Permissions
                permissions={permissions}
                onChange={handleChangePermission}
              />
            </div>
          </div>
          <footer className="userSettingsModalFooter">
            <Button size="large" onClick={closeModal}>
              Close
            </Button>
            <Button size="large" type="primary" htmlType="submit">
              Save Changes
            </Button>
            {/* <button
              type="button"
              className="btn danger-btn"
              onClick={() => setShowConfirmationModal(true)}
            >
              Disable Profile
            </button> */}
          </footer>
          {showConfirmationModal && (
            <DisableConfirmation
              open={showConfirmationModal}
              onCancel={onCloseConfirmationModal}
              onConfirm={handleDisableProfile}
            />
          )}
        </div>
      </Form>
    </>
  );
}
export default UserUpdate;
